/**
 * Setup (the step-by-step wizard): its steps, answers, checks, and what it sends. Pure, so tests can
 * run it without a browser. The page is src/pages/SetupWizard.tsx.
 *
 * Modes: "first" for a brand-new account, "again" for "Run setup again" (prefilled, saves), and
 * "preview" (prefilled, saves nothing). Skipping never invents anything about the person:
 * - Skip this step puts the step back as it was before you came to it (unknown for a new account,
 *   your current settings on "Run setup again"), units included, and moves on.
 * - Skip setup saves what's been entered so far: a cleared answer as unknown, a typo as the answer
 *   from before. On "Run setup again" everything else stays as it was; a new account gets setup's
 *   starting answers (targets once its basics are known, and fiber and sodium tracked).
 */
import type { SettingsView, SetupInput } from '../../shared/api';
import { APP_NAME } from '../../shared/brand';
import { addDays, type ISODate } from '../../shared/dates';
import { isBaseKey } from '../../shared/nutrients';
import { parseDecimal } from '../../shared/numbers';
import { ACTIVITY, ageFromBirthYear, bmrMifflin, MAX_GAIN_PACE_KG, MAX_PACE_KG, maxPaceKg, minHealthyGoalKg, missingBasics, paceCeilingKg, suggestTargets, weeksToGoal, type Activity, type Basic, type Sex, type SuggestedTargets } from '../../shared/targets';
import { formatWeight, fromDisplayWeight, KG_PER_LB, toDisplayWeight, type WeightUnit } from '../../shared/units';
import { formatPaceInput, paceInputError, parsePaceInput } from './pace';
import { heightText, parseBirthYear, parseHeight, parseWeightKg, type HeightText } from './profileFields';
import { tabStore, type SetupStore } from './setupStorage';

export type SetupMode = 'first' | 'again' | 'preview';
export type StepId = 'welcome' | 'about' | 'weight' | 'activity' | 'goal' | 'track' | 'ai' | 'done';
export type GoalChoice = 'lose' | 'maintain' | 'gain' | 'off';
export type Field = 'birthYear' | 'height' | 'weight' | 'goal' | 'goalWeight' | 'pace';
export type FieldErrors = Partial<Record<Field, string>>;

const STEP_IDS: readonly StepId[] = ['welcome', 'about', 'weight', 'activity', 'goal', 'track', 'ai', 'done'];
/** Steps with answers: these have Skip this step and Why we ask. Welcome and All set ask nothing. */
export const QUESTION_STEPS: readonly StepId[] = ['about', 'weight', 'activity', 'goal', 'track', 'ai'];

/** Extras offered in setup. Calories, protein, carbs and fat are always on. */
export const NUTRIENT_PICKS = ['fiber', 'sodium', 'added_sugar', 'sat_fat', 'potassium', 'calcium', 'iron', 'vitamin_d', 'vitamin_c', 'caffeine'] as const;
/** Ticked to start with on a first setup, as before the wizard. */
export const FIRST_PICKS: readonly string[] = ['fiber', 'sodium'];

/** Weekly paces offered: half-pound steps with pounds, quarter kilos with kilograms. */
const PACES: Record<WeightUnit, readonly number[]> = { lb: [0.5, 1, 1.5, 2].map((lb) => lb * KG_PER_LB), kg: [0.25, 0.5, 0.75, 1] };

/** What each activity level looks like, in the person's units. Calories, when we can, are added from their stats. */
const ACTIVITY_LOOK: Record<Activity, Record<WeightUnit, string>> = {
  sedentary: {
    lb: 'Around 3,000 steps a day, and little planned exercise',
    kg: 'Around 3,000 steps a day, and little planned exercise',
  },
  light: {
    lb: 'A 30-minute walk (about 1½ miles) on 2–3 days a week, or a daily total of around 6,000 steps',
    kg: 'A 30-minute walk (about 2.5 km) on 2–3 days a week, or a daily total of around 6,000 steps',
  },
  moderate: {
    lb: 'Jogging or a workout, about 45 minutes (roughly 2 miles) on 4 days a week, or around 9,000 steps a day',
    kg: 'Jogging or a workout, about 45 minutes (roughly 3 km) on 4 days a week, or around 9,000 steps a day',
  },
  active: {
    lb: 'A hard workout about an hour (roughly 4 miles of running) most days, or around 12,000 steps a day',
    kg: 'A hard workout about an hour (roughly 6 km of running) most days, or around 12,000 steps a day',
  },
  very_active: {
    lb: 'A physical job or two workouts a day, often 15,000 steps or several miles of training',
    kg: 'A physical job or two workouts a day, often 15,000 steps or several kilometres of training',
  },
};
const samePace = (x: number, y: number) => Math.abs(x - y) < 1e-6;

export interface Answers {
  unit: WeightUnit;
  name: string;
  sex: Sex | null;
  birthYear: string;
  ft: string;
  inch: string;
  cm: string;
  /** Today's weight as typed; blank for none. */
  weight: string;
  /** Mark today's weigh-in as the starting weight. Always so on a first setup; a choice on "Run setup again". */
  startHere: boolean;
  activity: Activity;
  goal: GoalChoice | null;
  goalWeight: string;
  paceKg: number;
  /** A pace typed instead of a preset, in the unit on screen (lb or kg a week). Blank when a preset is chosen. */
  paceCustom: string;
  tracked: string[];
  aiConsent: boolean;
}

export interface SetupContext {
  mode: SetupMode;
  /** Sign-in on: the AI step asks "I understand what's sent to Gemini". Local use doesn't ask. */
  signInOn: boolean;
  /** The AI step shows: AI is on for this server and account (perhaps waiting for agreement). */
  aiStep: boolean;
  /** The weight the goal step counts from without a weigh-in today: the current trend ("Run setup again"). */
  currentKg: number | null;
  /** The stored height ("Run setup again"), saved as it is unless the height is changed. */
  heightCm: number | null;
  /** The stored pace when it isn't one of setup's (it was set in You): offered too, so saving again never changes it. */
  customPaceKg: number | null;
  /** The extra nutrients the track step shows: the picks, plus any others already tracked. */
  picks: string[];
  thisYear: number;
}

export interface Progress {
  step: StepId;
  /** Steps finished with Next. */
  answered: StepId[];
  /** Steps skipped (put back as they were). */
  skipped: StepId[];
}

export interface SavedSetup extends Progress {
  v: 1;
  /** The day the answers were typed: a weight typed on another day isn't today's weigh-in. */
  day?: ISODate;
  answers: Answers;
}

// ——— Steps

export function stepsFor(ctx: Pick<SetupContext, 'aiStep'>): StepId[] {
  return STEP_IDS.filter((id) => id !== 'ai' || ctx.aiStep);
}

/** A step kept from an earlier visit, in these steps: itself, or the nearest one before it (the AI step goes when AI does). */
export function nearestStep(step: StepId, steps: readonly StepId[]): StepId {
  if (steps.includes(step)) return step;
  const earlier = STEP_IDS.slice(0, STEP_IDS.indexOf(step)).filter((s) => steps.includes(s));
  return earlier[earlier.length - 1] ?? steps[0] ?? 'welcome';
}

/** The AI step shows while AI is on for the server and the account, or only waits for this person to agree. */
export function showsAIStep(settings: Pick<SettingsView, 'ai'>): boolean {
  return settings.ai.offReason === null || settings.ai.offReason === 'consent';
}

export function contextFor(mode: SetupMode, settings: SettingsView, signInOn: boolean, thisYear = new Date().getFullYear()): SetupContext {
  const p = settings.profile;
  const tracked = settings.tracking.filter((t) => t.tracked && !isBaseKey(t.key)).map((t) => t.key);
  const extra = mode === 'first' ? [] : tracked.filter((key) => !(NUTRIENT_PICKS as readonly string[]).includes(key));
  const ownPace = mode !== 'first' && p.paceKgPerWeek > 0 && !PACES[p.weightUnit].some((kg) => samePace(kg, p.paceKgPerWeek));
  return {
    mode,
    signInOn,
    aiStep: showsAIStep(settings),
    currentKg: mode === 'first' ? null : settings.currentKg,
    heightCm: mode === 'first' ? null : p.heightCm,
    customPaceKg: ownPace ? p.paceKgPerWeek : null,
    picks: [...NUTRIENT_PICKS, ...extra],
    thisYear,
  };
}

/** Where a "Set a goal" visit starts: the first thing targets are missing, else the goal itself. */
export function goalStart(answers: Answers, ctx: SetupContext): StepId {
  const missing = missingFor(answers, ctx);
  if (missing.some((m) => m !== 'weight')) return 'about';
  return missing.includes('weight') ? 'weight' : 'goal';
}

// ——— Answers

/** Pounds where they're the everyday unit (the US, Liberia, Myanmar), kilograms elsewhere. */
export function defaultUnit(locale: string | undefined): WeightUnit {
  const region = /[-_]([A-Za-z]{2})(?:$|[-_])/.exec(locale ?? '')?.[1]?.toUpperCase();
  if (!region) return 'lb';
  return ['US', 'LR', 'MM'].includes(region) ? 'lb' : 'kg';
}

export function defaultPace(unit: WeightUnit): number {
  return unit === 'lb' ? KG_PER_LB : 0.5;
}

/** A brand-new account's answers: nothing known about the person. */
export function firstAnswers(unit: WeightUnit): Answers {
  return {
    unit,
    name: '',
    sex: null,
    birthYear: '',
    ft: '',
    inch: '',
    cm: '',
    weight: '',
    startHere: true,
    activity: 'light',
    goal: null,
    goalWeight: '',
    paceKg: defaultPace(unit),
    paceCustom: '',
    tracked: [...FIRST_PICKS],
    aiConsent: false,
  };
}

/**
 * Today's settings as answers, for "Run setup again" and the preview. No goal is chosen for you
 * when goals are your own (custom: choosing one replaces them), or when they're off on a "Set a
 * goal" visit (`pickGoal`), so the person picks one.
 */
export function answersFrom(settings: SettingsView, opts: { pickGoal?: boolean } = {}): Answers {
  const p = settings.profile;
  const unit = p.weightUnit;
  const kg = settings.currentKg;
  const mode = settings.goals.mode;
  const goal: GoalChoice | null =
    mode === 'custom' || (mode === 'off' && opts.pickGoal) ? null : mode === 'off' ? 'off' : p.goalKg === null ? 'maintain' : kg !== null && p.goalKg > kg ? 'gain' : 'lose';
  return {
    unit,
    name: p.name,
    sex: p.sex,
    birthYear: p.birthYear === null ? '' : String(p.birthYear),
    ...heightText(p.heightCm),
    weight: '',
    startHere: false,
    activity: p.activity,
    goal,
    goalWeight: p.goalKg === null ? '' : String(toDisplayWeight(p.goalKg, unit)),
    paceKg: p.paceKgPerWeek > 0 ? p.paceKgPerWeek : defaultPace(unit),
    paceCustom: '',
    tracked: settings.tracking.filter((t) => t.tracked && !isBaseKey(t.key)).map((t) => t.key),
    aiConsent: Boolean(p.aiConsentAt),
  };
}

/** The height's boxes as "Run setup again" fills them in: the stored height, shown rounded in every unit. */
function heightUntouched(a: HeightText, storedCm: number | null): boolean {
  if (storedCm === null) return false;
  const shown = heightText(storedCm);
  return a.ft === shown.ft && a.inch === shown.inch && a.cm === shown.cm;
}

/** What "Run setup again" starts from that switching units must never change: the stored height and your own pace. */
type Stored = Pick<SetupContext, 'heightCm' | 'customPaceKg'>;
const NOTHING_STORED: Stored = { heightCm: null, customPaceKg: null };

/** A typed pace in another unit. Something that isn't a number is left as it was, so the error stays. */
function paceTextIn(text: string | undefined, from: WeightUnit, to: WeightUnit): string {
  if (from === to || !text?.trim()) return text ?? '';
  const kg = parsePaceInput(text, from);
  return typeof kg === 'number' ? formatPaceInput(kg, to) : text;
}

/** A pace in another unit: a step becomes the same step there (1 lb ↔ ½ kg a week); your own pace, or any other, stays as it is. */
function paceIn(paceKg: number, from: WeightUnit, to: WeightUnit, own: number | null): number {
  if (own !== null && samePace(paceKg, own)) return paceKg;
  const i = PACES[from].findIndex((kg) => samePace(kg, paceKg));
  return from === to || i === -1 ? paceKg : PACES[to][i]!;
}

/**
 * Switching units keeps what's typed meaning the same thing: height and goal weight are converted,
 * and the pace keeps its step. An untouched stored height already reads right in every unit; a
 * cleared one is cleared in every unit.
 */
export function withUnit(a: Answers, unit: WeightUnit, stored: Stored = NOTHING_STORED): Answers {
  if (unit === a.unit) return a;
  const height = heightUntouched(a, stored.heightCm) ? 'untouched' : parseHeight(a);
  const weight = parseDecimal(a.weight);
  const goal = parseDecimal(a.goalWeight);
  const convert = (n: number | null, text: string) => (n !== null && n > 0 ? String(toDisplayWeight(fromDisplayWeight(n, a.unit), unit)) : text);
  return {
    ...a,
    unit,
    ...(height === 'untouched' || height === 'invalid' ? {} : heightText(height)),
    weight: convert(weight, a.weight),
    goalWeight: convert(goal, a.goalWeight),
    paceKg: paceIn(a.paceKg, a.unit, unit, stored.customPaceKg),
    paceCustom: paceTextIn(a.paceCustom, a.unit, unit),
  };
}

/** The answers of one step, taken from another set (Skip this step puts a step back as it started). */
export function stepAnswers(step: StepId, from: Answers): Partial<Answers> {
  switch (step) {
    case 'about':
      return { unit: from.unit, name: from.name, sex: from.sex, birthYear: from.birthYear, ft: from.ft, inch: from.inch, cm: from.cm };
    case 'weight':
      return { weight: from.weight, startHere: from.startHere };
    case 'activity':
      return { activity: from.activity };
    case 'goal':
      return { goal: from.goal, goalWeight: from.goalWeight, paceKg: from.paceKg, paceCustom: from.paceCustom };
    case 'track':
      return { tracked: [...from.tracked] };
    case 'ai':
      return { aiConsent: from.aiConsent };
    default:
      return {};
  }
}

/** "Skip this step": put the step back as it was before (unknown for a new account) and remember it was skipped. */
export function skipStep(
  step: StepId,
  answers: Answers,
  initial: Answers,
  progress: Pick<Progress, 'answered' | 'skipped'>,
  ctx: Stored = NOTHING_STORED,
): { answers: Answers; answered: StepId[]; skipped: StepId[] } {
  const restored: Answers = { ...answers, ...stepAnswers(step, initial) };
  return {
    // The units go back too, with anything typed in them since (a weight, a goal) converted back.
    answers: step === 'about' ? withUnit({ ...restored, unit: answers.unit }, initial.unit, ctx) : restored,
    answered: progress.answered.filter((s) => s !== step),
    skipped: [...new Set([...progress.skipped, step])],
  };
}

// ——— Reading what's typed (profile fields: src/lib/profileFields.ts)

const valid = <T>(v: T | 'invalid'): T | null => (v === 'invalid' ? null : v);

/** What suggested targets are worked out from. */
interface Basics {
  sex: Sex | null;
  birthYear: number | null;
  heightCm: number | null;
  currentKg: number | null;
}

type BasicsContext = Pick<SetupContext, 'currentKg' | 'heightCm' | 'thisYear'>;

/** The goal weight typed, in kg, or null. */
export function typedGoalKg(a: Pick<Answers, 'goalWeight' | 'unit'>): number | null {
  const n = parseDecimal(a.goalWeight);
  return n !== null && n > 0 ? fromDisplayWeight(n, a.unit) : null;
}

/** The weight setup counts from: today's, if typed, or the current trend ("Run setup again"). */
export function currentWeight(a: Answers, ctx: Pick<SetupContext, 'currentKg'>): number | null {
  return valid(parseWeightKg(a.weight, a.unit)) ?? ctx.currentKg;
}

/** The height the boxes mean: the stored one, exactly, while they show it untouched (they round it), else what's typed. */
function answerHeight(a: Answers, ctx: Pick<SetupContext, 'heightCm'>): number | null | 'invalid' {
  return heightUntouched(a, ctx.heightCm) ? ctx.heightCm : parseHeight(a);
}

/** The basics as typed, as the steps show them: anything that doesn't check out counts as unknown. */
function typedBasics(a: Answers, ctx: BasicsContext): Basics {
  return { sex: a.sex, birthYear: valid(parseBirthYear(a.birthYear, ctx.thisYear)), heightCm: valid(answerHeight(a, ctx)), currentKg: currentWeight(a, ctx) };
}

const missingIn = (b: Basics): Basic[] => missingBasics(b, b.currentKg);

/** The goal as it applies: without every basic, "No goal, just count" is the only one there is. */
function choiceFor(goal: GoalChoice | null, b: Basics): GoalChoice | null {
  return missingIn(b).length > 0 ? 'off' : goal;
}

/** The basics suggested targets still need, from the answers. */
export function missingFor(a: Answers, ctx: BasicsContext): Basic[] {
  return missingIn(typedBasics(a, ctx));
}

/**
 * What this activity level looks like for the person filling in setup. Steps and a bit of
 * exercise, in their units. When sex, age, height and weight are all known, also the extra
 * calories a day that level adds above resting: BMR × (activity factor − 1), the same multiplier
 * targets use. Anything skipped falls back to the example without a calorie figure.
 */
export function activityExample(level: Activity, a: Answers, ctx: BasicsContext): string {
  const picture = ACTIVITY_LOOK[level][a.unit];
  const b = typedBasics(a, ctx);
  if (b.sex === null || b.birthYear === null || b.heightCm === null || b.currentKg === null) return `${picture}.`;
  const bmr = bmrMifflin(b.sex, b.currentKg, b.heightCm, ageFromBirthYear(b.birthYear, ctx.thisYear));
  const extra = Math.round((bmr * (ACTIVITY[level].factor - 1)) / 10) * 10;
  return `${picture}. About ${extra.toLocaleString('en-US')} extra kcal a day for you.`;
}

/** The step to go back to for a missing basic. */
export function stepFor(basic: Basic): StepId {
  return basic === 'weight' ? 'weight' : 'about';
}

// ——— The goal

/**
 * Weekly paces offered, up to 1% of the weight a week (losing) or ½ kg (gaining): the unit's steps,
 * plus `extra` (a pace of your own, from You) so it can stay.
 */
export function paceOptions(unit: WeightUnit, direction: 'lose' | 'gain', currentKg: number | null, extra: number | null = null): number[] {
  const cap = direction === 'lose' ? (currentKg !== null ? maxPaceKg(currentKg) : 1) : MAX_GAIN_PACE_KG;
  const all = extra !== null && extra > 0 && !PACES[unit].some((kg) => samePace(kg, extra)) ? [...PACES[unit], extra].sort((x, y) => x - y) : [...PACES[unit]];
  return all.filter((kg) => kg <= cap + 1e-9);
}

/**
 * The pace these answers use. A pace typed in (and within the ceiling) is kept, the same as a
 * preset. Otherwise the chosen preset if it's offered, else the nearest slower one offered
 * (never a faster one), else the slowest.
 */
export function effectivePace(a: Answers, direction: 'lose' | 'gain', currentKg: number | null, extra: number | null = null): number {
  const custom = parsePaceInput(a.paceCustom, a.unit);
  if (typeof custom === 'number' && custom <= paceCeilingKg(direction) + 1e-9) return custom;
  const options = paceOptions(a.unit, direction, currentKg, extra);
  if (options.some((kg) => samePace(kg, a.paceKg))) return a.paceKg;
  const slower = options.filter((kg) => kg < a.paceKg);
  return slower[slower.length - 1] ?? options[0] ?? 0;
}

/** The goal as it applies: without every basic, "No goal, just count" is the only one there is. */
export function goalChoice(a: Answers, ctx: BasicsContext): GoalChoice | null {
  return choiceFor(a.goal, typedBasics(a, ctx));
}

// ——— Checks (Next)

/** What's wrong with a step's answers. Blank basics are fine (unknown); typed ones must make sense. */
export function checkStep(step: StepId, a: Answers, ctx: SetupContext): FieldErrors {
  const errors: FieldErrors = {};
  if (step === 'about') {
    if (parseBirthYear(a.birthYear, ctx.thisYear) === 'invalid') errors.birthYear = `Enter a year from 1900 to ${ctx.thisYear - 13}.`;
    if (parseHeight(a) === 'invalid') {
      errors.height = a.unit === 'lb' ? 'Enter a height from 3 ft 4 in to 8 ft 2 in.' : 'Enter a height from 100 to 250 cm.';
    }
  }
  if (step === 'weight' && parseWeightKg(a.weight, a.unit) === 'invalid') {
    errors.weight = a.unit === 'lb' ? 'Enter a weight from 45 to 880 lb.' : 'Enter a weight from 20 to 400 kg.';
  }
  if (step === 'goal') {
    const b = typedBasics(a, ctx);
    const choice = choiceFor(a.goal, b);
    if (choice === null) errors.goal = 'Choose one to go on, or skip this step.';
    if (choice === 'lose' || choice === 'gain') {
      const message = goalWeightProblem(a, choice, b);
      if (message) errors.goalWeight = message;
      // Blank uses a preset. A typed pace has to be a positive number within the ceiling.
      if (a.paceCustom?.trim()) {
        const paceMessage = paceInputError(a.paceCustom, a.unit, choice);
        if (paceMessage) errors.pace = paceMessage;
      }
    }
  }
  return errors;
}

function goalWeightProblem(a: Answers, choice: 'lose' | 'gain', b: Basics): string | null {
  const kg = b.currentKg;
  const goalKg = typedGoalKg(a);
  if (goalKg === null) return `Enter your goal weight, like ${a.unit === 'lb' ? '165' : '72'}.`;
  if (goalKg < 30 || goalKg > 300) return a.unit === 'lb' ? 'Enter a goal from 67 to 660 lb.' : 'Enter a goal from 30 to 300 kg.';
  if (kg !== null && choice === 'lose' && goalKg > kg - 0.5) return `To lose weight, choose a goal below ${formatWeight(kg, a.unit, { decimals: 0 })}.`;
  if (kg !== null && choice === 'gain' && goalKg < kg + 0.5) return `To gain weight, choose a goal above ${formatWeight(kg, a.unit, { decimals: 0 })}.`;
  if (b.heightCm !== null && goalKg < minHealthyGoalKg(b.heightCm)) {
    return `That's below a healthy weight for your height (BMI 18.5). Choose ${formatWeight(Math.ceil(minHealthyGoalKg(b.heightCm) * 10) / 10, a.unit, { decimals: 0 })} or more.`;
  }
  if (paceOptions(a.unit, choice, kg).length === 0) return `${APP_NAME} can't suggest a safe pace for that goal.`;
  return null;
}

// ——— Previews

/** The targets these answers would give, for the goal step's live preview. Null while a basic is missing or with no goal. */
export function previewTargets(a: Answers, ctx: SetupContext): SuggestedTargets | null {
  const b = typedBasics(a, ctx);
  const choice = choiceFor(a.goal, b);
  if (choice === null || choice === 'off') return null;
  if (b.sex === null || b.birthYear === null || b.heightCm === null || b.currentKg === null) return null;
  const changing = (choice === 'lose' || choice === 'gain') && goalWeightProblem(a, choice, b) === null;
  const goalKg = changing ? typedGoalKg(a) : null;
  return suggestTargets({
    sex: b.sex,
    birthYear: b.birthYear,
    heightCm: b.heightCm,
    activity: a.activity,
    currentKg: b.currentKg,
    goalKg,
    paceKgPerWeek: goalKg !== null ? effectivePace(a, choice === 'gain' ? 'gain' : 'lose', b.currentKg, ctx.customPaceKg) : 0,
    unit: a.unit,
    year: ctx.thisYear,
  });
}

/** Roughly when the goal is reached at the chosen pace, or null without one. */
export function reachDate(a: Answers, ctx: SetupContext, today: ISODate, targets: SuggestedTargets | null): ISODate | null {
  const kg = currentWeight(a, ctx);
  const goalKg = typedGoalKg(a);
  if (!targets || kg === null || goalKg === null || targets.direction === 'maintain') return null;
  const weeks = weeksToGoal(kg, goalKg, targets.effectivePaceKgPerWeek);
  return weeks === null ? null : addDays(today, weeks * 7);
}

// ——— What's saved

/**
 * Whether the goal step's answers differ from where they started. Compared in the unit now chosen
 * (converted the same way), and only what the goal uses: a goal weight within 0.1 kg (unit
 * rounding) and the same pace count as the same.
 */
function goalChanged(a: Answers, initial: Answers, ctx: Stored, currentKg: number | null): boolean {
  const start = withUnit(initial, a.unit, ctx);
  if (a.goal !== start.goal) return true;
  if (a.goal !== 'lose' && a.goal !== 'gain') return false;
  const kg = typedGoalKg(a);
  const was = typedGoalKg(start);
  if (kg === null || was === null) return kg !== was;
  return Math.abs(kg - was) >= 0.1 || !samePace(effectivePace(a, a.goal, currentKg, ctx.customPaceKg), effectivePace(start, a.goal, currentKg, ctx.customPaceKg));
}

/**
 * The goal the answers ask for, from the basics that will be saved. Null when there's nothing to
 * ask for: none chosen, a goal weight that doesn't check out, or a basic missing (targets then
 * come once it's known), unless that's "No goal, just count" chosen outright.
 */
function requestedGoal(a: Answers, b: Basics, ctx: Pick<SetupContext, 'customPaceKg'>): SetupInput['goal'] | null {
  if (a.goal === 'off') return { mode: 'off' };
  if (missingIn(b).length > 0) return null;
  if (a.goal === 'maintain') return { mode: 'maintain' };
  if ((a.goal === 'lose' || a.goal === 'gain') && goalWeightProblem(a, a.goal, b) === null) {
    return { mode: 'change', goalKg: Math.round((typedGoalKg(a) ?? 0) * 100) / 100, paceKgPerWeek: effectivePace(a, a.goal, b.currentKg, ctx.customPaceKg) };
  }
  return null;
}

/** Whether a setup saved with this request shows targets: every basic known, and a goal other than "No goal". */
export function savesTargets(req: SetupInput, ctx: Pick<SetupContext, 'currentKg'>): boolean {
  if (req.goal.mode === 'off') return false;
  return missingBasics(req, req.weighIn?.kg ?? ctx.currentKg).length === 0;
}

/**
 * What Finish (or Skip setup) sends, or null for a preview, which saves nothing. A first setup
 * sends every answer. On "Run setup again" the goal and AI consent are sent only when they were
 * changed (an untouched goal keeps custom targets, your own pace and the exact goal weight), and
 * an untouched height is saved exactly. A cleared answer is saved as unknown; a typo keeps the
 * answer from before.
 */
export function setupRequest(a: Answers, initial: Answers, ctx: SetupContext, env: { today: ISODate; timezone: string }): SetupInput | null {
  if (ctx.mode === 'preview') return null;
  const keep = <T>(typed: T | 'invalid', before: T | null): T | null => (typed === 'invalid' ? before : typed);
  const before = typedBasics(initial, ctx);
  const weightKg = valid(parseWeightKg(a.weight, a.unit));
  // Worked out once: the goal is chosen from the same basics that are saved.
  const saved: Basics = {
    sex: a.sex,
    birthYear: keep(parseBirthYear(a.birthYear, ctx.thisYear), before.birthYear),
    heightCm: keep(answerHeight(a, ctx), before.heightCm),
    currentKg: weightKg ?? ctx.currentKg,
  };

  const first = ctx.mode === 'first';
  const fallback: SetupInput['goal'] = first ? { mode: 'maintain' } : { mode: 'keep' };
  const goal = first || goalChanged(a, initial, ctx, saved.currentKg) ? (requestedGoal(a, saved, ctx) ?? fallback) : fallback;

  const asksConsent = ctx.aiStep && ctx.signInOn;
  const aiConsent = !asksConsent ? null : first || a.aiConsent !== initial.aiConsent ? a.aiConsent : null;

  return {
    name: a.name.trim().slice(0, 40),
    weightUnit: a.unit,
    sex: saved.sex,
    birthYear: saved.birthYear,
    heightCm: saved.heightCm,
    activity: a.activity,
    weighIn: weightKg === null ? null : { kg: weightKg, startingWeight: first ? true : a.startHere },
    goal,
    tracked: ctx.picks.map((key) => ({ key, tracked: a.tracked.includes(key) })),
    aiConsent,
    timezone: env.timezone,
    today: env.today,
  };
}

// ——— Keeping answers through a reload (sessionStorage; the keys are in ./setupStorage.ts)

const isStep = (v: unknown): v is StepId => typeof v === 'string' && (STEP_IDS as readonly string[]).includes(v);
const isText = (v: unknown): v is string => typeof v === 'string' && v.length <= 80;

/**
 * Answers saved earlier on this tab, merged over `base` field by field (anything malformed is
 * ignored). Saved on another day than `today`, the weight typed then isn't today's, so it's dropped.
 */
export function loadSetup(key: string, base: Answers, store: SetupStore | undefined = tabStore(), today?: ISODate): SavedSetup | null {
  let raw: string | null = null;
  try {
    raw = store?.getItem(key) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const saved = JSON.parse(raw) as Partial<SavedSetup> | null;
    if (!saved || saved.v !== 1 || !isStep(saved.step) || typeof saved.answers !== 'object' || saved.answers === null) return null;
    const s = saved.answers as Partial<Record<keyof Answers, unknown>>;
    const day = typeof saved.day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(saved.day) ? saved.day : undefined;
    const sameDay = today === undefined || day === today;
    const answers: Answers = {
      unit: s.unit === 'kg' || s.unit === 'lb' ? s.unit : base.unit,
      name: isText(s.name) ? s.name.slice(0, 40) : base.name,
      sex: s.sex === 'female' || s.sex === 'male' || s.sex === 'other' || s.sex === null ? s.sex : base.sex,
      birthYear: isText(s.birthYear) ? s.birthYear : base.birthYear,
      ft: isText(s.ft) ? s.ft : base.ft,
      inch: isText(s.inch) ? s.inch : base.inch,
      cm: isText(s.cm) ? s.cm : base.cm,
      weight: sameDay && isText(s.weight) ? s.weight : base.weight,
      startHere: typeof s.startHere === 'boolean' ? s.startHere : base.startHere,
      activity: typeof s.activity === 'string' && ['sedentary', 'light', 'moderate', 'active', 'very_active'].includes(s.activity) ? (s.activity as Activity) : base.activity,
      goal: s.goal === null || (typeof s.goal === 'string' && ['lose', 'maintain', 'gain', 'off'].includes(s.goal)) ? (s.goal as GoalChoice | null) : base.goal,
      goalWeight: isText(s.goalWeight) ? s.goalWeight : base.goalWeight,
      paceKg: typeof s.paceKg === 'number' && s.paceKg >= 0 && s.paceKg <= MAX_PACE_KG ? s.paceKg : base.paceKg,
      paceCustom: isText(s.paceCustom) ? s.paceCustom : '',
      tracked: Array.isArray(s.tracked) ? s.tracked.filter((k): k is string => typeof k === 'string').slice(0, 100) : base.tracked,
      // Agreeing to AI is ticked in the visit it counts for: a box ticked in an abandoned run never comes back ticked.
      aiConsent: base.aiConsent,
    };
    const steps = (v: unknown) => (Array.isArray(v) ? v.filter(isStep) : []);
    return { v: 1, ...(day ? { day } : {}), step: saved.step, answers, answered: steps(saved.answered), skipped: steps(saved.skipped) };
  } catch {
    return null;
  }
}

export function saveSetup(key: string, value: SavedSetup, store: SetupStore | undefined = tabStore()): void {
  try {
    store?.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or blocked: setup still works, it just won't survive a reload.
  }
}

export function clearSetup(key: string, store: SetupStore | undefined = tabStore()): void {
  try {
    store?.removeItem(key);
  } catch {
    // Nothing to clear where storage is blocked.
  }
}
